import { computed, ref } from '@vue/composition-api';

import type { Task } from '@/types';
import { useClient } from '@/use';
import { taskIsFinished } from '@/utils';
import store from '@/store';

// Initiate Analysis Menu
const initiateAnalysisOpen = ref(false);

// Completed Analysis Menu
const completedAnalysesOpen = ref(false);
const tasks = ref<Task[]>([]);
const latestTasks = ref<Task[]>([]);
const latestTaskTotal = ref(0);
const ongoingTasks = computed(() => latestTasks.value.filter((task) => !taskIsFinished(task)));
const taskPollingActive = ref(false);
let taskPollingTimer: number | null = null;
let taskPollingInFlight = false;

async function pollLatestTasks() {
  if (taskPollingInFlight) return;
  if (!store.state.client || !store.state.client.serverURL) {
    taskPollingActive.value = false;
    return;
  }
  taskPollingInFlight = true;
  try {
    const result = await useClient().fetchUserTasks(1, 15);
    latestTasks.value = result.tasks;
    latestTaskTotal.value = result.total;
    taskPollingActive.value = ongoingTasks.value.length > 0;
  } catch (error) {
    // Keep the badge state while a transient polling failure is retried.
    console.warn('Failed to refresh analysis task status', error);
  } finally {
    taskPollingInFlight = false;
  }

  if (taskPollingActive.value && document.visibilityState === 'visible') {
    taskPollingTimer = window.setTimeout(() => {
      taskPollingTimer = null;
      pollLatestTasks();
    }, 15000);
  }
}

document.addEventListener('visibilitychange', () => {
  if (taskPollingTimer !== null) {
    window.clearTimeout(taskPollingTimer);
    taskPollingTimer = null;
  }
  if (document.visibilityState === 'visible' && taskPollingActive.value) pollLatestTasks();
});

function startLatestTaskPolling() {
  if (taskPollingTimer !== null) {
    window.clearTimeout(taskPollingTimer);
    taskPollingTimer = null;
  }
  taskPollingActive.value = true;
  pollLatestTasks();
}

export {
  initiateAnalysisOpen,
  completedAnalysesOpen,
  tasks,
  latestTasks,
  latestTaskTotal,
  ongoingTasks,
  taskPollingActive,
  startLatestTaskPolling,
};
